/**
 * QL-313 — Equity Share Chart Widget.
 *
 * Self-contained: fetches its own state, renders the goal's equity share over
 * time as a 100 %-normalised stacked bar (Equity / Non-Equity / Unknown) with
 * the risk model's ceiling as a rule, and handles the accept / block deviation
 * UI through the shared DeviationDecisionCard. Used on the advisor's goal page.
 *
 * Sibling of volatility-chart-widget.tsx: same states, same cards, same
 * invalidation; only the measure and the chart differ.
 */

import { pickT } from "@repo/shared";
import type { EquityShareDecisionValue } from "@repo/shared/types";
import {
  ASSET_CLASS_FORECAST_HORIZONS,
  type AssetClassForecastHorizon,
  selectAssetClassForecastPoints,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { NormalizedStackedBarChart } from "@/components/charts/normalized-stacked-bar-chart";
import { Button } from "@/components/ui/button";
import { ChartLegendStrip } from "@/components/ui/chart-legend-strip";
import { ComplianceSummaryCard } from "@/components/ui/compliance-summary-card";
import { DeviationDecisionCard } from "@/components/ui/deviation-decision-card";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  getEquityShareChartState,
  saveEquityShareChartDecision,
} from "@/lib/api/flow-instances/equity-share-chart";
import { formatForecastGridDate } from "@/lib/date-utils";
import { flowCompletionQueryKey } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { type StackedBarDatum, TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";
import { T } from "./equity-share-chart-widget.i18n";

/** The unknown band, the same grey the asset-class forecast uses for its unknown band. */
const UNKNOWN_COLOR = "#9ca3af";

// ── Widget ───────────────────────────────────────────────────────────────────

type EquityShareChartWidgetProps = {
  instanceId: string;
  goalId: string;
  config?: {
    style?: string;
    title?: string;
    description?: string;
    enabledHorizons?: AssetClassForecastHorizon[];
    defaultHorizon?: string;
    showTable?: boolean;
  };
  language: string;
  chartColors?: string[] | null;
  hasValidationError?: boolean;
  /** True on a locked (submitted / completed) instance: the decision is shown, never edited. */
  disabled?: boolean;
};

export function EquityShareChartWidget({
  instanceId,
  goalId,
  config,
  language,
  chartColors,
  hasValidationError,
  disabled = false,
}: EquityShareChartWidgetProps) {
  const t = pickT(T, language);
  const qc = useQueryClient();

  const {
    data: state,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: goalSnapshotQueryKeys.equityShareChart(goalId),
    queryFn: () => getEquityShareChartState(instanceId, goalId),
  });

  const enabledHorizons: AssetClassForecastHorizon[] = config?.enabledHorizons?.length
    ? config.enabledHorizons
    : [...ASSET_CLASS_FORECAST_HORIZONS];
  const configuredDefault = config?.defaultHorizon as AssetClassForecastHorizon | undefined;
  const defaultHorizon =
    configuredDefault && enabledHorizons.includes(configuredDefault)
      ? configuredDefault
      : (enabledHorizons[0] ?? "1Y");
  const [horizon, setHorizon] = useState<AssetClassForecastHorizon>(defaultHorizon);

  // Decision state — synced from server, editable locally until Save
  const [decision, setDecision] = useState<EquityShareDecisionValue | "">("");
  const [justification, setJustification] = useState("");
  useEffect(() => {
    setDecision(state?.decision?.decision ?? "");
    setJustification(state?.decision?.justification ?? "");
  }, [state?.decision?.decision, state?.decision?.justification]);

  const saveMutation = useMutation({
    mutationFn: (args: { decision: EquityShareDecisionValue | null; justification?: string }) =>
      saveEquityShareChartDecision(instanceId, goalId, args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalSnapshotQueryKeys.equityShareChart(goalId) });
      qc.invalidateQueries({ queryKey: flowCompletionQueryKey(instanceId) });
    },
  });

  const handleSave = async (value: EquityShareDecisionValue | null) => {
    try {
      const result = await saveMutation.mutateAsync({
        decision: value,
        ...(value === "accepted" ? { justification } : {}),
      });
      if (!result.success) {
        toast.error(t.error, { description: result.error });
        return;
      }
      toast.success(t.decisionSaved);
    } catch (err) {
      toast.error(t.error, { description: err instanceof Error ? err.message : undefined });
    }
  };

  // The shared horizon rule: the anchor year's end plus the year-ends, five or
  // ten bars, baseline first when present. The PDF block draws the same
  // selection for the range its designer picked.
  const visiblePoints = useMemo(
    () => (state?.points ? selectAssetClassForecastPoints(state.points, horizon) : []),
    [state?.points, horizon],
  );

  // The gating point: the strip under the chart names the bands with THESE
  // figures, the ones the check is made on.
  const year1Point = useMemo(
    () => state?.points.find((p) => p.kind === "anniversary") ?? null,
    [state?.points],
  );
  // The chips say which date that is on hover: the anniversary is drawn
  // nowhere, so without it the figures match no bar in the 5Y / 10Y views.
  const year1Hint = year1Point?.date
    ? t.year1Hint(formatForecastGridDate(year1Point.date, language))
    : undefined;

  const maxUnknownPct = useMemo(() => {
    if (!visiblePoints.length) return 0;
    return Math.max(...visiblePoints.map((p) => p.unknownPct));
  }, [visiblePoints]);

  const seriesOrder = [t.equity, t.nonEquity, t.unknown];
  const palette = chartColors?.length ? chartColors : TABLEAU20_COLORS;
  const seriesColors = [palette[0]!, palette[1 % palette.length]!, UNKNOWN_COLOR];

  const chartData = useMemo<StackedBarDatum[]>(() => {
    const rows: StackedBarDatum[] = [];
    for (const p of visiblePoints) {
      rows.push({ x: p.label, series: t.equity, value: p.equitySharePct });
      rows.push({ x: p.label, series: t.nonEquity, value: p.nonEquitySharePct });
      if (p.unknownPct > 0) rows.push({ x: p.label, series: t.unknown, value: p.unknownPct });
    }
    return rows;
  }, [visiblePoints, t.equity, t.nonEquity, t.unknown]);

  // Empty / error states follow the sibling snapshot widgets: a dashed
  // placeholder for "nothing to show yet", a red-bordered block for a failure.
  // The configured style wraps the chart only, as it does for them.
  if (isLoading) return null;

  if (isError) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 flex items-center gap-2 text-sm text-destructive">
        {t.fetchError}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => refetch()}
        >
          {t.retry}
        </Button>
      </div>
    );
  }

  // QL-493 — the summary card's labels, incl. the not-evaluable state.
  const summaryLabels = {
    year1Label: t.year1Label,
    coverageWarning: t.coverageWarning,
    breachMessage: t.breachMessage,
    notEvaluableNoRiskScore: t.notEvaluableNoRiskScore,
    notEvaluableNoSnapshot: t.notEvaluableNoSnapshot,
    notEvaluableMissingAssetData: t.notEvaluableMissingAssetData,
  };

  if (!state?.enabled) {
    // QL-493 — no forecast, but a ceiling applies: say so as the block it is,
    // not as an empty chart. Without a reason there is nothing to check.
    if (state?.notEvaluableReason) {
      return (
        <div className={hasValidationError ? "border border-destructive rounded-lg p-4" : ""}>
          <FlowComponentHeader title={config?.title} description={config?.description} />
          <ComplianceSummaryCard
            title={t.equityShare}
            labels={summaryLabels}
            effective={null}
            notEvaluableReason={state.notEvaluableReason}
            year1Pct={null}
            ceilingPct={null}
            unknownPct={0}
            deviationAcceptanceEnabled={state.deviationAcceptanceEnabled}
          />
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noData}
      </div>
    );
  }

  const effective = state.effective;

  const componentStyle = config?.style ?? "none";
  const wrapperClass = hasValidationError
    ? "border border-destructive rounded-lg p-4"
    : componentStyle === "border"
      ? "border rounded-lg p-4"
      : componentStyle === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  // The data table, one row per drawn bar in bar order — the same shape as
  // the volatility chart's table (decided 2026-09-25 over the spec's
  // band-by-band legend). The baseline row comes first, greyed; the
  // anniversary gate point is not a bar and is not a row: the summary card
  // below carries the year-1 figure against the ceiling.
  const tableRows = visiblePoints.filter((p) => p.kind !== "baseline" && p.kind !== "anniversary");
  const baselineRow = visiblePoints.find((p) => p.kind === "baseline") ?? null;
  const showUnknownColumn = visiblePoints.some((p) => p.unknownPct > 0);
  const showTable = config?.showTable !== false;

  return (
    <div className={wrapperClass}>
      <FlowComponentHeader title={config?.title} description={config?.description} />
      <div className="space-y-4 mt-2">
        {/* Horizon toggle — hidden when only one range is enabled */}
        {enabledHorizons.length > 1 && (
          <div className="flex justify-end">
            <ToggleGroup
              type="single"
              value={horizon}
              onValueChange={(next) => {
                if (next) setHorizon(next as AssetClassForecastHorizon);
              }}
              className="h-9"
            >
              {enabledHorizons.map((h) => (
                <ToggleGroupItem key={h} value={h} className="h-7 px-3 text-xs">
                  {t[`horizon${h}` as const]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}

        {/* Stacked bar: Equity / Non-Equity / Unknown, ceiling as a rule */}
        {chartData.length > 0 ? (
          <NormalizedStackedBarChart
            data={chartData}
            seriesOrder={seriesOrder}
            seriesColors={seriesColors}
            lang={language}
            // The legend strip below names the bands with their year-1 figures.
            showLegend={false}
            referenceLine={
              state.maxEquitySharePct != null
                ? {
                    value: state.maxEquitySharePct,
                    label: `${t.ceilingLabel} ${state.maxEquitySharePct.toFixed(1)}%`,
                    color: "#dc2626",
                  }
                : undefined
            }
          />
        ) : (
          <div className="p-4 text-muted-foreground text-sm">{t.noDataPoints}</div>
        )}

        {/* Legend strip: swatch, band, year-1 value — the legend and the gating
            figures in one line, so no second table sits next to the data table.
            The labels say "Year 1" and the hover says the date, as the
            volatility strip does. */}
        {chartData.length > 0 && (
          <ChartLegendStrip
            items={[
              {
                key: "equity",
                label: t.year1Equity,
                value: year1Point ? `${year1Point.equitySharePct.toFixed(1)}%` : "–",
                color: seriesColors[0]!,
                hint: year1Hint,
                tone:
                  state.maxEquitySharePct != null &&
                  year1Point != null &&
                  year1Point.equitySharePct > state.maxEquitySharePct
                    ? "destructive"
                    : "default",
              },
              {
                key: "nonEquity",
                label: t.year1NonEquity,
                value: year1Point ? `${year1Point.nonEquitySharePct.toFixed(1)}%` : "–",
                color: seriesColors[1]!,
                hint: year1Hint,
              },
              ...(showUnknownColumn || (year1Point?.unknownPct ?? 0) > 0
                ? [
                    {
                      key: "unknown",
                      label: t.year1Unknown,
                      value: year1Point ? `${year1Point.unknownPct.toFixed(1)}%` : "–",
                      color: UNKNOWN_COLOR,
                      hint: year1Hint,
                    },
                  ]
                : []),
              ...(state.maxEquitySharePct != null
                ? [
                    {
                      key: "max",
                      label: t.ceilingLabel,
                      value: `${state.maxEquitySharePct.toFixed(1)}%`,
                      color: "#dc2626",
                      glyph: "line" as const,
                    },
                  ]
                : []),
            ]}
          />
        )}

        {/* Data table (configurable): one row per bar, equity checked against the ceiling */}
        {showTable && tableRows.length > 0 && (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.date}</TableHead>
                <TableHead className="text-right">{t.equity} (%)</TableHead>
                <TableHead className="text-right">{t.nonEquity} (%)</TableHead>
                {showUnknownColumn && <TableHead className="text-right">{t.unknown} (%)</TableHead>}
                {state.maxEquitySharePct != null && (
                  <TableHead className="text-right">{t.vsMax}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {baselineRow && (
                <TableRow className="bg-muted/20">
                  <TableCell className="text-muted-foreground">{baselineRow.label}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {baselineRow.equitySharePct.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {baselineRow.nonEquitySharePct.toFixed(1)}%
                  </TableCell>
                  {showUnknownColumn && (
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {baselineRow.unknownPct.toFixed(1)}%
                    </TableCell>
                  )}
                  {state.maxEquitySharePct != null && <TableCell />}
                </TableRow>
              )}
              {tableRows.map((p) => {
                const aboveCeiling =
                  state.maxEquitySharePct != null && p.equitySharePct > state.maxEquitySharePct;
                const diff =
                  state.maxEquitySharePct != null
                    ? p.equitySharePct - state.maxEquitySharePct
                    : null;
                return (
                  <TableRow key={p.label}>
                    <TableCell>{p.label}</TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${aboveCeiling ? "text-destructive font-medium" : ""}`}
                    >
                      {p.equitySharePct.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {p.nonEquitySharePct.toFixed(1)}%
                    </TableCell>
                    {showUnknownColumn && (
                      <TableCell className="text-right tabular-nums">
                        {p.unknownPct.toFixed(1)}%
                      </TableCell>
                    )}
                    {state.maxEquitySharePct != null && (
                      <TableCell
                        className={`text-right tabular-nums text-xs ${aboveCeiling ? "text-destructive" : "text-emerald-600"}`}
                      >
                        {aboveCeiling ? "✕" : "✓"}{" "}
                        {diff != null
                          ? diff >= 0
                            ? `+${diff.toFixed(1)}%`
                            : `${diff.toFixed(1)}%`
                          : ""}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* Card 1: Summary — the shared card (QL-313), as the decision card is */}
        <ComplianceSummaryCard
          title={t.equityShare}
          labels={{ ...summaryLabels, baselineLabel: t.baselineLabel }}
          effective={effective}
          notEvaluableReason={state.notEvaluableReason}
          year1Pct={state.year1EquitySharePct}
          ceilingPct={state.maxEquitySharePct}
          baselinePct={state.baselineEquitySharePct}
          ceilingChange={state.ceilingChangedSinceDecision}
          unknownPct={maxUnknownPct}
          deviationAcceptanceEnabled={state.deviationAcceptanceEnabled}
        />

        {/* Card 2: Deviation decision (only when breach + acceptance enabled) */}
        {(effective === "deviation" || effective === "accepted" || effective === "not_accepted") &&
          state.deviationAcceptanceEnabled && (
            <DeviationDecisionCard
              idPrefix={`esc-${goalId}`}
              value={decision}
              onValueChange={setDecision}
              justification={justification}
              onJustificationChange={setJustification}
              hasStoredDecision={state.decision !== null}
              onSave={(value) => handleSave(value)}
              onClear={() => handleSave(null)}
              isSaving={saveMutation.isPending}
              disabled={disabled}
            />
          )}
      </div>
    </div>
  );
}
